import {
  MemberEntryModalArenaTable,
  MemberEntryModalContent,
  MemberEntryModalFooter,
  MemberEntryModalHeader,
  MemberEntryModalStatus
} from '@library/components/modals/MemberEntryModal';
import { useCallback, useMemo, useState } from 'react';
import type { FormattedNewWager } from '@library/types/newWagers';
import { MessageBanner } from '@library/components/banners/MessageBanner';
import { Modal } from '@library/components/modals/Modal';
import { formatNewWagerRefundError } from '@bo-utils/newWagers';
import { useMemberNewWagers } from '@bo-hooks/members';

export interface MemberEntryModalProps {
  canRefundEntry: boolean;
  isError?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  onCancelModal: () => void;
  selectedNewWager: FormattedNewWager | undefined;
}

export const MemberEntryModal = ({
  canRefundEntry,
  isError = false,
  isLoading = false,
  isOpen,
  onCancelModal,
  selectedNewWager
}: MemberEntryModalProps) => {
  const [isArenaTableContentVisible, setIsArenaTableContentVisible] =
    useState(false);

  const {
    isRefundLoading,
    onRefundNewWager,
    refundError,
    resetRefundNewWager
  } = useMemberNewWagers();

  const formattedRefundError = useMemo(
    () => (refundError ? formatNewWagerRefundError(refundError) : undefined),
    [refundError]
  );

  const isRefundBtnDisabled = isRefundLoading || !!formattedRefundError;

  const selectedNewWagerContestId = selectedNewWager?.newWager.contestId;

  const { lineup, newWager } = selectedNewWager || {};
  const showWagerDetails =
    !isError && !isLoading && selectedNewWager && lineup && newWager;

  const handleCloseToast = useCallback(() => {
    resetRefundNewWager();
  }, [resetRefundNewWager]);

  return (
    <Modal
      isOpen={isOpen}
      onCancelModal={onCancelModal}
      testId="member-entry-modal"
    >
      <MemberEntryModalStatus
        isError={isError}
        isLoading={isLoading}
        selectedNewWager={selectedNewWager}
      />
      {showWagerDetails && (
        <div className="bg-soFresh-110 border-soFresh-140 relative mx-auto w-[70rem] flex-col rounded-md border">
          <MemberEntryModalHeader
            isArenaTableContentVisible={isArenaTableContentVisible}
            lineup={lineup}
            newWager={newWager}
            onCancelModal={onCancelModal}
            onShowArenaTableContent={setIsArenaTableContentVisible}
          />
          <div className="flex h-[calc(100vh-350px)] justify-center p-6">
            {isArenaTableContentVisible && !!selectedNewWagerContestId ? (
              <MemberEntryModalArenaTable
                selectedNewWagerContestId={selectedNewWagerContestId}
                entryId={newWager?.id}
              />
            ) : (
              <MemberEntryModalContent newWagerDetails={selectedNewWager} />
            )}
          </div>
          <MemberEntryModalFooter
            canRefundEntry={canRefundEntry}
            isRefundBtnDisabled={isRefundBtnDisabled}
            newWager={newWager}
            onRefundNewWager={onRefundNewWager}
          />
        </div>
      )}
      {!!formattedRefundError && (
        <MessageBanner
          className="whitespace-pre-line"
          code="ERROR"
          message={formattedRefundError}
          onCancel={handleCloseToast}
        />
      )}
    </Modal>
  );
};
